import {IDriver} from 'app/shared/model/driver.model';
import {IAmbulance} from 'app/shared/model/ambulance.model';
import {IJourney} from 'app/shared/model/journey.model';
import {IMMamaSettings} from 'app/shared/model/mmama-settings-model';
import {IVillage} from 'app/shared/model/village.model';
import {IHealthcareFacility} from 'app/shared/model/healthcare-facility.model';
import {RouteStartType} from 'app/shared/model/enumerations/route-start-type.model';
import {RouteEndType} from 'app/shared/model/enumerations/route-end-type.model';
import {JourneyDriverType} from 'app/entities/dispatcher-dashboard/components/journey-info';
import routeService from 'app/entities/route/route.service';
import geofenceRouteService from 'app/entities/geofence-route/geofence-route.service';

export interface CostCalculationResult {
  cost: number;
  distance: number;
  duration: number;
}

export interface RouteTypeConfig {
  startType: RouteStartType;
  endType: RouteEndType;
  startId: string;
  endId: string;
}

/**
 * Helper function to calculate route cost from route data
 */
export const calculateRouteCost = (route: any, costKm: number): number => {
  return route.overridePrice && route.overridePrice > 0
    ? route.overridePrice
    : (route.distance || 0) * costKm;
};

/**
 * Helper function to get route cost via the backend location directions API.
 */
export const getMapboxRouteCost = async (
  routeType: RouteTypeConfig,
  costKm: number
): Promise<CostCalculationResult> => {
  const startFacilityId = routeType.startType === RouteStartType.HEALTHCARE_FACILITY ? routeType.startId : undefined;
  const startVillageId = routeType.startType === RouteStartType.VILLAGE ? routeType.startId : undefined;
  const endFacilityId = routeType.endType === RouteEndType.HEALTHCARE_FACILITY ? routeType.endId : undefined;
  const endVillageId = routeType.endType === RouteEndType.VILLAGE ? routeType.endId : undefined;

  const result = await routeService.getLocationDirections(startFacilityId, startVillageId, endFacilityId, endVillageId);

  if (result) {
    const distanceKm = parseFloat(result.distance.toFixed(2));
    const durationSeconds = result.durationSeconds;
    return {
      cost: parseFloat((distanceKm * costKm).toFixed(2)),
      distance: distanceKm,
      duration: durationSeconds
    };
  } else {
    throw new Error('Unable to calculate route. Please select a different driver or try again later.');
  }
};

/**
 * Helper function to handle geofence route fallback
 */
export const getGeofenceRouteCost = async (
  startCouncilId: string,
  endCouncilId: string,
  costKm: number
): Promise<CostCalculationResult> => {
  let geofenceRoutes = await geofenceRouteService.searchEntities(startCouncilId, endCouncilId);

  if ((!geofenceRoutes || geofenceRoutes.length === 0) && startCouncilId !== endCouncilId) {
    geofenceRoutes = await geofenceRouteService.searchEntities(endCouncilId, startCouncilId);
  }

  if (geofenceRoutes && geofenceRoutes.length > 0) {
    const geofenceRoute = geofenceRoutes[0]; // Use the first available route
    return {
      cost: geofenceRoute.overridePrice && geofenceRoute.overridePrice > 0
        ? geofenceRoute.overridePrice
        : (geofenceRoute.distance || 0) * costKm,
      distance: geofenceRoute.distance || null,
      duration: geofenceRoute.durationSeconds || null
    };
  }

  throw new Error('Unable to calculate route cost. No routes available between selected locations.');
};

/**
 * Helper function to handle mapbox route with fallback to geofence
 */
export const getRouteWithFallback = async (
  startLocation: IVillage | IHealthcareFacility,
  endLocation: IVillage | IHealthcareFacility,
  costKm: number,
  useGeofence: boolean,
  geofenceSaveRoutes: boolean,
  routeType: RouteTypeConfig,
  createRouteCallback?: (routeData: any) => void
): Promise<CostCalculationResult> => {
  try {
    const mapboxRouteCost = await getMapboxRouteCost(routeType, costKm);

    // If geofencing is enabled and saving routes is allowed, save the routes
    if (useGeofence && geofenceSaveRoutes && createRouteCallback) {
      createRouteCallback({
        durationSeconds: mapboxRouteCost.duration,
        distance: mapboxRouteCost.distance,
        isActive: true,
        startType: routeType.startType,
        endType: routeType.endType,
        startVillageId: routeType.startType === RouteStartType.VILLAGE ? routeType.startId : null,
        startFacilityId: routeType.startType === RouteStartType.HEALTHCARE_FACILITY ? routeType.startId : null,
        endVillageId: routeType.endType === RouteEndType.VILLAGE ? routeType.endId : null,
        endFacilityId: routeType.endType === RouteEndType.HEALTHCARE_FACILITY ? routeType.endId : null,
        description: `Auto-generated route`
      });
    }

    return mapboxRouteCost;
  } catch (error) {
    if (useGeofence && geofenceSaveRoutes) {
      // Fetch geofence routes if available
      try {
        const startCouncilId = startLocation?.council?.id;
        const endCouncilId = endLocation?.council?.id;

        if (startCouncilId && endCouncilId) {
          return await getGeofenceRouteCost(startCouncilId, endCouncilId, costKm);
        }

        // If no council IDs available, throw the original error
        console.error('No geofence routes found and Mapbox failed:', error);
        throw new Error('Unable to calculate route cost. No routes available between selected locations.');
      } catch (geofenceError) {
        console.error('Error fetching geofence routes:', geofenceError);
        throw new Error('Unable to calculate route cost using available route systems.');
      }
    } else {
      console.error('Error fetching Mapbox route cost:', error);
      throw new Error('Unable to calculate route cost using Mapbox.');
    }
  }
};

/**
 * Helper function to calculate Route 1 cost (Driver Home Location -> Pickup Location)
 */
export const calculateRoute1Cost = async (
  journey: IJourney,
  homeLocation: IVillage | IHealthcareFacility | null,
  costKm: number,
  flatFee: number,
  useGeofence: boolean,
  geofenceSaveRoutes: boolean,
  createRouteCallback?: (routeData: any) => void
): Promise<CostCalculationResult> => {
  if (!homeLocation) {
    return {cost: flatFee, distance: 0, duration: 0};
  }

  if (journey.startVillage && journey.startVillage.id) {
    // Pickup from Village
    homeLocation = homeLocation as IVillage;
    if (homeLocation.id === journey.startVillage.id) {
      return { cost: 0, distance: null, duration: null }; // Driver is already at pickup location
    }

    if (flatFee) {
      return { cost: flatFee, distance: 0, duration: 0 }; // If flat fee is applicable, return it directly
    }

    const route1Routes = await routeService.getRoutesFromVillageToVillage(
      homeLocation.id,
      journey.startVillage.id
    );

    if (route1Routes.length > 0) {
      return { cost: calculateRouteCost(route1Routes[0], costKm), distance: route1Routes[0].distance || null, duration: route1Routes[0].durationSeconds || null };
    } else {
      const route1Result = await getRouteWithFallback(
        homeLocation,
        journey.startVillage,
        costKm,
        useGeofence,
        geofenceSaveRoutes,
        {
          startType: RouteStartType.VILLAGE,
          endType: RouteEndType.VILLAGE,
          startId: homeLocation.id,
          endId: journey.startVillage.id
        },
        createRouteCallback
      );
      return { cost: route1Result.cost, distance: route1Result.distance || null, duration: route1Result.duration || null };
    }
  } else if (journey.startHealthcareFacility && journey.startHealthcareFacility.id) {
    // Pickup from Healthcare Facility
    homeLocation = homeLocation as IHealthcareFacility;

    if (homeLocation.id === journey.startHealthcareFacility.id) {
      return { cost: 0, distance: null, duration: null }; // Driver is already at pickup location
    }

    if (flatFee) {
      return { cost: flatFee, distance: 0, duration: 0 }; // If flat fee is applicable, return it directly
    }

    const route1Routes = await routeService.getRoutesFromHealthcareFacilityToHealthcareFacility(
      homeLocation.id,
      journey.startHealthcareFacility.id
    );

    if (route1Routes.length > 0) {
      return { cost: calculateRouteCost(route1Routes[0], costKm), distance: route1Routes[0].distance || null, duration: route1Routes[0].durationSeconds || null };
    } else {
      const route1Result = await getRouteWithFallback(
        homeLocation,
        journey.startHealthcareFacility,
        costKm,
        useGeofence,
        geofenceSaveRoutes,
        {
          startType: RouteStartType.HEALTHCARE_FACILITY,
          endType: RouteEndType.HEALTHCARE_FACILITY,
          startId: homeLocation.id,
          endId: journey.startHealthcareFacility.id
        },
        createRouteCallback
      );
      return { cost: route1Result.cost, distance: route1Result.distance || null, duration: route1Result.duration || null };
    }
  } else {
    throw new Error('Invalid pickup location.');
  }
};

/**
 * Helper function to calculate Route 2 cost for geofence (Pickup Location -> Dropoff Location)
 */
export const calculateRoute2CostGeofence = async (
  journey: IJourney,
  costKm: number,
  useGeofence: boolean,
  geofenceSaveRoutes: boolean,
  createRouteCallback?: (routeData: any) => void
): Promise<CostCalculationResult> => {
  if (journey.route && journey.route.id) {
    // If route is already defined, use it
    const route = journey.route;
    return {
      cost: calculateRouteCost(route, costKm),
      distance: route.distance || null,
      duration: route.durationSeconds || null
    };
  }

  if (journey.startVillage && journey.endVillage) {
    // Village to Village
    const route2Routes = await routeService.getRoutesFromVillageToVillage(
      journey.startVillage.id,
      journey.endVillage.id
    );

    if (route2Routes.length > 0) {
      const route = route2Routes[0];
      return {
        cost: calculateRouteCost(route, costKm),
        distance: route.distance || null,
        duration: route.durationSeconds || null
      };
    } else {
      return await getRouteWithFallback(
        journey.startVillage,
        journey.endVillage,
        costKm,
        useGeofence,
        geofenceSaveRoutes,
        {
          startType: RouteStartType.VILLAGE,
          endType: RouteEndType.VILLAGE,
          startId: journey.startVillage.id,
          endId: journey.endVillage.id
        },
        createRouteCallback
      );
    }
  } else if (journey.startVillage && journey.endHealthcareFacility) {
    // Village to Healthcare Facility
    const route2Routes = await routeService.getRoutesFromVillageToHealthcareFacility(
      journey.startVillage.id,
      journey.endHealthcareFacility.id
    );

    if (route2Routes.length > 0) {
      const route = route2Routes[0];
      return {
        cost: calculateRouteCost(route, costKm),
        distance: route.distance || null,
        duration: route.durationSeconds || null
      };
    } else {
      return await getRouteWithFallback(
        journey.startVillage,
        journey.endHealthcareFacility,
        costKm,
        useGeofence,
        geofenceSaveRoutes,
        {
          startType: RouteStartType.VILLAGE,
          endType: RouteEndType.HEALTHCARE_FACILITY,
          startId: journey.startVillage.id,
          endId: journey.endHealthcareFacility.id
        },
        createRouteCallback
      );
    }
  } else if (journey.startHealthcareFacility && journey.endHealthcareFacility) {
    // Healthcare Facility to Healthcare Facility
    const route2Routes = await routeService.getRoutesFromHealthcareFacilityToHealthcareFacility(
      journey.startHealthcareFacility.id,
      journey.endHealthcareFacility.id
    );

    if (route2Routes.length > 0) {
      const route = route2Routes[0];
      return {
        cost: calculateRouteCost(route, costKm),
        distance: route.distance || null,
        duration: route.durationSeconds || null
      };
    } else {
      return await getRouteWithFallback(
        journey.startHealthcareFacility,
        journey.endHealthcareFacility,
        costKm,
        useGeofence,
        geofenceSaveRoutes,
        {
          startType: RouteStartType.HEALTHCARE_FACILITY,
          endType: RouteEndType.HEALTHCARE_FACILITY,
          startId: journey.startHealthcareFacility.id,
          endId: journey.endHealthcareFacility.id
        },
        createRouteCallback
      );
    }
  } else {
    // Healthcare Facility to Village - NOT SUPPORTED
    throw new Error('Healthcare Facility to Village journeys are not supported.');
  }
};

/**
 * Helper function to get the effective cost per kilometer, prioritizing vehicle type over country settings.
 */
export const getEffectiveCostKm = (driver: IDriver | IAmbulance, countrySettings: IMMamaSettings): number => {
  const vehicleCostKm = driver?.vehicleType?.costKm;
  const settingsCostKm = countrySettings?.costKm || 0;

  // Use vehicle type cost/km if it's greater than 0, otherwise use settings
  return (vehicleCostKm && vehicleCostKm > 0) ? vehicleCostKm : settingsCostKm;
};

/**
 * Helper function to get the effective flat driver fee, prioritizing vehicle type over country settings.
 */
export const getEffectiveFlatFee = (driver: IDriver | IAmbulance, countrySettings: IMMamaSettings): number => {
  const vehicleFlatFee = driver?.vehicleType?.flatFee;
  const settingsFlatFee = countrySettings?.flatDriverFee || 0;

  // Use vehicle type flat fee if it's greater than 0, otherwise use settings
  return (vehicleFlatFee && vehicleFlatFee > 0) ? vehicleFlatFee : settingsFlatFee;
};

/**
 * Main cost calculation function for payments.
 * Used by both driver-selection-modal and payment-detail.
 */
export const calculatePaymentCost = async (
  journey: IJourney,
  driver: IDriver | IAmbulance,
  driverType: JourneyDriverType,
  countrySettings: IMMamaSettings,
  createRouteCallback?: (routeData: any) => void
): Promise<CostCalculationResult> => {
  if (!driver || !countrySettings || !journey.id) {
    throw new Error('Unable to calculate cost. Please ensure all necessary information is provided.');
  }

  const costKm = getEffectiveCostKm(driver, countrySettings);
  const flatFee = getEffectiveFlatFee(driver, countrySettings);
  const useGeofence = countrySettings?.useGeofence || false;
  const geofenceSaveRoutes = countrySettings?.geofenceSaveRoutes || false;
  const returnFee = countrySettings?.returnFee || false;
  const homeLocation = driverType === JourneyDriverType.COMMUNITY
    ? journey.startVillage?.id ? (driver as IDriver).homeVillage : (driver as IDriver).homeHealthcareFacility // community driver
    : journey.startHealthcareFacility?.id ? (driver as IAmbulance).healthcareFacility : null; // ambulance driver

  // Calculate pickup route (from driver's location to pickup point)
  const route1Result = await calculateRoute1Cost(journey, homeLocation, costKm, flatFee, useGeofence, geofenceSaveRoutes, createRouteCallback);

  // Calculate destination route (from pickup point to final destination)
  let route2Cost;
  let distance = journey.totalDistance || 0;
  let duration = journey.totalDurationSeconds || 0;

  if (useGeofence) {
    // Calculate route 2 cost using geofence-enabled method
    const route2Result = await calculateRoute2CostGeofence(journey, costKm, useGeofence, geofenceSaveRoutes, createRouteCallback);
    route2Cost = route2Result.cost;
    if (route2Result.distance !== null && route2Result.distance !== undefined) {
      distance = route2Result.distance;
    }
    if (route2Result.duration !== null && route2Result.duration !== undefined) {
      duration = route2Result.duration;
    }
  } else {
    if (!journey.route) {
      throw new Error('Journey route is not defined. Please select a different pickup/destination.');
    }
    route2Cost = calculateRouteCost(journey.route, costKm);
    if (journey.route.distance !== null && journey.route.distance !== undefined) {
      distance = journey.route.distance;
    }
    if (journey.route.durationSeconds !== null && journey.route.durationSeconds !== undefined) {
      duration = journey.route.durationSeconds;
    }
  }

  if (route1Result.distance !== null && route1Result.distance !== undefined) {
    distance += route1Result.distance;
  }

  return {
    cost: returnFee ? parseFloat(((route1Result.cost + route2Cost) * 2).toFixed(2)) : parseFloat((route1Result.cost + route2Cost).toFixed(2)),
    distance: distance || 0,
    duration: duration || 0,
  };
};

/**
 * Simplified cost calculation function for payments when driver is known from journey
 */
export const calculateJourneyCost = async (
  journey: IJourney,
  countrySettings: IMMamaSettings,
  createRouteCallback?: (routeData: any) => void
): Promise<CostCalculationResult> => {
  if (!journey || !countrySettings) {
    throw new Error('Unable to calculate cost. Journey or country settings not provided.');
  }

  // Determine driver and driver type from journey
  let driver: IDriver | IAmbulance;
  let driverType: JourneyDriverType;

  if (journey.ambulance) {
    driver = journey.ambulance;
    driverType = JourneyDriverType.AMBULANCE;
  } else if (journey.driver) {
    driver = journey.driver;
    driverType = JourneyDriverType.COMMUNITY;
  } else {
    throw new Error('No driver or ambulance found in journey.');
  }

  return await calculatePaymentCost(journey, driver, driverType, countrySettings, createRouteCallback);
};
